import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { FiX } from 'react-icons/fi';
import './AgentModal.css';

const AgentModal = ({ agent, onClose, isEmployee = false }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: isEmployee ? 'PERMANENT_EMPLOYEE' : 'AGENT',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (agent) {
      setFormData({
        username: agent.username || '',
        email: agent.email || '',
        password: '',
        password2: '',
        first_name: agent.first_name || '',
        last_name: agent.last_name || '',
        phone: agent.phone || '',
        role: agent.role || (isEmployee ? 'PERMANENT_EMPLOYEE' : 'AGENT'),
      });
    }
  }, [agent]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (agent) {
        // Update existing agent (without password, password2, and username)
        const updateData = {
          email: formData.email,
          role: formData.role,
        };
        // Only include optional fields if they have values
        if (formData.first_name?.trim()) updateData.first_name = formData.first_name.trim();
        if (formData.last_name?.trim()) updateData.last_name = formData.last_name.trim();
        if (formData.phone?.trim()) updateData.phone = formData.phone.trim();
        await usersAPI.update(agent.id, updateData);
      } else {
        // Create new agent
        if (formData.password !== formData.password2) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (!formData.password || formData.password.length < 8) {
          setError('Password must be at least 8 characters long');
          setLoading(false);
          return;
        }
        const createData = {
          username: formData.username.trim(),
          email: formData.email.trim(),
          password: formData.password,
          role: formData.role,
        };
        // Only include optional fields if they have values
        if (formData.first_name?.trim()) createData.first_name = formData.first_name.trim();
        if (formData.last_name?.trim()) createData.last_name = formData.last_name.trim();
        if (formData.phone?.trim()) createData.phone = formData.phone.trim();
        await usersAPI.create(createData);
      }
      onClose();
    } catch (err) {
      // Extract error message from response
      const errorData = err.response?.data;
      let errorMessage = 'Failed to save agent/employee';
      
      if (errorData) {
        // Handle field-specific errors
        if (typeof errorData === 'object') {
          const errorMessages = [];
          for (const [key, value] of Object.entries(errorData)) {
            if (Array.isArray(value)) {
              // Format field name nicely
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessages.push(`${fieldName}: ${value.join(', ')}`);
            } else if (typeof value === 'string') {
              errorMessages.push(value);
            } else if (typeof value === 'object' && value !== null) {
              // Handle nested objects
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessages.push(`${fieldName}: ${JSON.stringify(value)}`);
            }
          }
          errorMessage = errorMessages.length > 0 ? errorMessages.join('; ') : errorMessage;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      console.error('Error saving agent/employee:', err.response?.data || err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {agent 
              ? (isEmployee ? 'Edit Employee' : 'Edit Agent')
              : (isEmployee ? 'Create New Employee' : 'Create New Agent')
            }
          </h2>
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="agent-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-row">
            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={!!agent}
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Role *</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                disabled={!!agent}
              >
                <option value="AGENT">Agent</option>
                <option value="PERMANENT_EMPLOYEE">Permanent Employee</option>
              </select>
            </div>
          </div>

          {!agent && (
            <div className="form-row">
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!agent}
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  name="password2"
                  value={formData.password2}
                  onChange={handleChange}
                  required={!agent}
                  minLength={8}
                />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : agent ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentModal;

